import { XmdData } from "../../types/xmd";
import debug from "debug";

const log = debug("app:xmd");
const CACHE_KEY = "xmdVaultCache";

type Meta = {
  name: string;
  uuid: string;
  savedAt: string;
};

type CachedPayload = {
  xmd: XmdData;
  meta: Meta;
};

type CacheBlob = CachedPayload | XmdData;

type LoadProjectMessage = {
  event: "loadProject";
  contents: string | XmdData;
  name?: string;
  uuid?: string;
};

class XmdCacheManager {
  private experience: any;
  private eventHub: any;

  constructor(experience: any) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.initListeners();
  }

  private initListeners() {
    // Restore from session cache on demand
    this.eventHub.on("check-xmd-cache", () => {
      log("check-xmd-cache received");
      const cached = sessionStorage.getItem(CACHE_KEY);

      if (cached) {
        try {
          const parsed = JSON.parse(cached) as CacheBlob;

          // Support both wrapped {xmd, meta} and legacy raw XMD blobs
          const xmdData: XmdData =
            (parsed as CachedPayload)?.xmd ?? (parsed as XmdData);

          const meta: Partial<Meta> =
            (parsed as CachedPayload)?.meta ?? {};

          // Apply project meta to UI
          const nameFromCache = meta.name || "Recovered Design";
          const nameInput = document.getElementById(
            "toolbar-project-name"
          ) as HTMLInputElement | null;
          const nameContainer = document.getElementById(
            "toolbar-project-name-container"
          );
          if (nameInput) nameInput.value = nameFromCache;
          if (nameContainer) (nameContainer as HTMLElement).style.display = "flex";

          // Stash uuid in memory for tracker
          if (meta.uuid) {
            this.experience.projectUuid = meta.uuid;
            (window as any).currentProjectUuid = meta.uuid;
          }

          // Load XMD through normal pipeline
          this.eventHub.emit("MakePartsLoaderEvent", {
            eventType: "load",
            data: xmdData,
          });

          log("Loaded cached XMD (with meta) from sessionStorage.");
        } catch (error) {
          console.error("Failed to parse cached XMD:", error);
        }
      } else {
        log("No cached XMD found");
      }

      // Signal tracker it's safe to start saving
      setTimeout(() => {
        this.eventHub.emit("xmd-loaded");
      }, 600);
    });

    // Accept project loads via eventHub (replaces postMessage)
    this.eventHub.on("loadProject", (eventData: LoadProjectMessage) => {
      try {
        const { contents, name, uuid } = eventData;

        // contents may be a string or an object
        const xmd: XmdData =
          typeof contents === "string" ? (JSON.parse(contents) as XmdData) : (contents as XmdData);

        // Apply name to UI
        const nameInput = document.getElementById(
          "toolbar-project-name"
        ) as HTMLInputElement | null;
        const nameContainer = document.getElementById(
          "toolbar-project-name-container"
        );
        if (nameInput) nameInput.value = name || "Recovered Design";
        if (nameContainer) (nameContainer as HTMLElement).style.display = "flex";

        // Persist uuid/name in memory so tracker reuses them
        if (uuid) {
          this.experience.projectUuid = uuid;
          (window as any).currentProjectUuid = uuid;
        }

        // Load XMD
        this.eventHub.emit("MakePartsLoaderEvent", {
          eventType: "load",
          data: xmd,
        });

        // Cache wrapped payload (backward compatible)
        const payload: CachedPayload = {
          xmd,
          meta: {
            name: name || "Recovered Design",
            uuid: uuid || "",
            savedAt: new Date().toISOString(),
          },
        };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));

        // Tracker can now start saving
        setTimeout(() => this.eventHub.emit("xmd-loaded"), 0);

        log("Loaded project via eventHub(loadProject) and cached meta+xmd.");
      } catch (err) {
        console.error("Failed handling loadProject event:", err);
      }
    });
  }
}

export default XmdCacheManager;
